import { useAuth0 } from '@auth0/auth0-react';
import { useFeedbackItemCalls } from 'components/Common/ThumbsDown';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import SaveUpdatesToFolder from 'components/Dashboard/SubNav/ui/SaveUpdatesToFolder';
import { store } from 'components/Store';
import { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { useContext, useEffect, useRef, useState } from 'react';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const MainListActionsBar = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { selectedUpdates, activePlan } = state;

  const { enableReports } = activePlan ?? {};
  const dataHubUser = dataHubUsers(activePlan);
  const { setIsOpenReportPopup, setIsOpenLoggedOut } = props ?? {};
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const timeoutRef = useRef(null);

  const { inboxSection, dashboardSection } = useMainSectionParameter();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (selectedUpdates?.length > 0) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => setIsVisible(true), 10);
    } else if (shouldRender) {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => setShouldRender(false), 250);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [selectedUpdates, shouldRender]);

  const embargoedUpdates = selectedUpdates?.some((item) => item?.embargoed);

  const addToReportButton = (
    <button
      className={`rounded-pill px-4 specific-y-padding-5 hovered-button bg-main-white general-button d-none d-lg-inline-block ${
        enableReports && !embargoedUpdates && !dataHubUser ? 'text-light-blue' : 'light-50-text'
      }`}
      onClick={() => {
        if (!embargoedUpdates && !dataHubUser) {
          if (enableReports && isAuthenticated) {
            setIsOpenReportPopup(true);
          } else {
            setIsOpenLoggedOut(true);
          }
        }
      }}
      data-tooltip-content={
        embargoedUpdates
          ? 'One or more updates selected are currently under embargo. You’ll be able to add them to reports once the embargo has been lifted.'
          : ''
      }
      data-tooltip-id='general-tooltip'
      data-tooltip-class-name='feed-tooltip'
      data-tooltip-place='bottom'
    >
      <span className='icon-document specific-right-margin-10 paragraph-p2' />
      Add to a report{' '}
      {(!enableReports || dataHubUser) && <span className='icon-padlock ml-2 paragraph-p2 d-inline-block-centered' />}
    </button>
  );

  return (
    <>
      {shouldRender && (
        <div
          className={`main-list-action-bar bg-light-blue position-absolute top-0 adjustment-of-position w-100 overflow-hidden ${
            isVisible ? 'visible' : ''
          }`}
        >
          <div className='mx-auto main-content-wrapper flex-centered justify-content-between py-3 resize-elements-container overflow-auto'>
            <div className='side-left-element pl-lg-5 pl-4 d-none d-lg-block text-white'>
              <div className='flex-centered'>
                <SelectedCounter value={selectedUpdates?.length} />
                <span>selected</span>
              </div>
            </div>
            <div
              className={`main-content flex-centered position-relative justify-content-between filters-general-container pr-xl-5 px-md-4 px-3 `}
            >
              <div className='flex-centered'>
                <button
                  className={`rounded-pill px-4 specific-y-padding-5 hovered-button bg-main-white general-button d-none d-lg-inline-block ${
                    enableReports && !embargoedUpdates ? 'text-light-blue' : 'light-50-text'
                  }`}
                  onClick={() => {
                    if (!embargoedUpdates) {
                      if (enableReports && isAuthenticated) {
                        setIsOpenReportPopup(true);
                      } else {
                        setIsOpenLoggedOut(true);
                      }
                    }
                  }}
                  data-tooltip-content={
                    embargoedUpdates
                      ? 'One or more updates selected are currently under embargo. You’ll be able to add them to reports once the embargo has been lifted.'
                      : ''
                  }
                  data-tooltip-id='general-tooltip'
                  data-tooltip-class-name='feed-tooltip'
                  data-tooltip-place='bottom'
                >
                  <span className='icon-document specific-right-margin-10 paragraph-p2' />
                  Add to a report{' '}
                  {!enableReports && <span className='icon-padlock ml-2 paragraph-p2 d-inline-block-centered' />}
                </button>
                <SaveUpdatesToFolder />
                {/* {(inboxSection || dashboardSection) && <MarkUpdatesAsIrrelevant />} */}
              </div>
              <button
                className='rounded-pill px-4 specific-y-padding-5 hovered-button bg-main-white general-button nowrap-item ml-3'
                onClick={resetSelectedUpdatesState}
              >
                Cancel <span className='icon-close paragraph-p5 ml-2' />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MarkUpdatesAsIrrelevant = () => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { selectedUpdates } = state ?? {};
  const { contentResults, setContentResults } = useContentResults();

  const sectionName = createSectionName();
  const section = contentResults[sectionName];

  const [isLoading, setIsLoading] = useState(false);
  const { addFeedback } = useFeedbackItemCalls();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();
  const { dashboardSection, priorityUpdatesSection } = useMainSectionParameter();

  const markAsIrrelevant = async () => {
    if (!isLoading) {
      try {
        setIsLoading(true);
        for (let i = 0; i < selectedUpdates?.length; i++) {
          const item = selectedUpdates[i];
          const { parentObjectID, objectID } = item ?? {};
          await addFeedback({ objectId: parentObjectID ?? objectID });
        }
        if (dashboardSection || priorityUpdatesSection) {
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'dasboardPriorityUpdatesFeedback',
            value: selectedUpdates.map((item) => ({
              objectId: item?.parentObjectID ?? item?.objectID,
              feedback: 'Negative',
            })),
          });
        } else {
          let newSection = { ...section };
          selectedUpdates?.forEach((item) => {
            const index = newSection?.hits?.findIndex((hit) => hit?.id === item?.id);
            if (index !== undefined && index >= 0) {
              newSection.feedItemFlags[index] = {
                ...newSection.feedItemFlags?.[index],
                ratingScored: 'Negative',
              };
            }
          });
          let newContentResults = {
            ...contentResults,
            [sectionName]: newSection,
          };
          setContentResults(newContentResults);
        }
        resetSelectedUpdatesState();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  return (
    <button
      className='rounded-pill px-4 specific-y-padding-5 hovered-button bg-main-white general-button text-light-blue nowrap-item'
      onClick={markAsIrrelevant}
    >
      {isLoading ? (
        <i className='fas fa-spinner fa-spin mr-2 d-inline-block-centered' />
      ) : (
        <span className='icon-thumbs-down-middle paragraph-p2 specific-right-margin-12' />
      )}
      Mark as irrelevant
    </button>
  );
};

const useMainActionsBarFunctions = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { temporarySelectedUpdates, selectedUpdates, selectedUpdatesIds } = state ?? {};
  const resetSelectedUpdatesState = () => {
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: [] });
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: [] });
  };
  const restoreSelectedUpdates = () => {
    if (temporarySelectedUpdates) {
      const { selectedUpdates, selectedUpdatesIds } = temporarySelectedUpdates;
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: selectedUpdates });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: selectedUpdatesIds });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'temporarySelectedUpdates', value: null });
    }
  };
  const setTemporarySelectedUpdates = () => {
    if (selectedUpdates?.length && selectedUpdatesIds?.length) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'temporarySelectedUpdates',
        value: { selectedUpdates, selectedUpdatesIds },
      });
    }
  };
  return { resetSelectedUpdatesState, restoreSelectedUpdates, setTemporarySelectedUpdates };
};

const SelectedCounter = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const [anim, setAnim] = useState(null); // { prev, next, dir: 'inc'|'dec', key }

  useEffect(() => {
    if (value === display) return;
    const dir = value > display ? 'inc' : 'dec';
    setAnim({ prev: display, next: value, dir, key: Date.now() });
  }, [value, display]);

  const handleDone = () => {
    if (!anim) return;
    setDisplay(anim.next);
    setAnim(null);
  };

  return (
    <span className={`position-relative d-inline-block scounter ${value?.toString()?.length > 1 ? 'mr-1' : ''}`}>
      {/* idle state */}
      {!anim && <span className='scounter__static flex-centered'>{display}</span>}

      {/* animated swap */}
      {anim && (
        <>
          <span key={anim.key + '-out'} className={`flex-centered scounter__layer scounter__out scounter__${anim.dir}`}>
            {anim.prev}
          </span>
          <span
            key={anim.key + '-in'}
            className={`flex-centered scounter__layer scounter__in scounter__${anim.dir}`}
            onAnimationEnd={handleDone}
          >
            {anim.next}
          </span>
        </>
      )}
    </span>
  );
};

export { useMainActionsBarFunctions };
export default MainListActionsBar;
